import isEqual from "lodash.isequal";
import { DateTime } from "luxon";

import {
  checkoutUpsellConversionGraphConfig,
  checkoutUpsellProductsGraphConfig,
  checkoutUpsellRevenueGraphConfig,
  newConversionGraphConfig,
  newCustomMetricsGraphConfig,
  newDataReconciliationGraphConfig,
  newDiscountGraphConfig,
  newOrdersGraphConfig,
  newOverviewGraphConfig,
  newPostTestMetricsGraphConfig,
  newPostTestMetricsIncludeInTestOrdersGraphConfig,
  newProductGroupsGraphConfig,
  newProfitGraphConfig,
  newRevenueGraphConfig,
  newShippingGraphConfig,
  newSubscriptionsGraphConfig,
  onsiteUpsellProductsGraphConfig,
  postPurchaseConversionGraphConfig,
  postPurchaseProductsGraphConfig,
  postPurchaseRevenueGraphConfig,
} from "@intelligems/ig-graphs/graph-configs";
import { NewHorizStackedBarColors } from "@intelligems/ig-graphs/colors";
import {
  AnalyzeTinybirdResourceResponseBodyType,
  BaseTinybirdMetricsType,
} from "@intelligems/ig-types/functions/analytics/analyze-resource-tinybird/analyze-resource-tinybird";
import { ExperienceActionDto } from "@intelligems/ig-types/dtos/experience-actions";
import {
  GraphConfig,
  GraphGlobalMetric,
} from "@intelligems/ig-types/schemas/analytics/custom-graphs";
import { StandardEventList } from "@intelligems/ig-types/dtos/analytics-events/standard-events";
import { Theme } from "@mui/material";
import { filterDashboardDataTb } from "@src/api/analytics/dashboard-tinybird";
import type { DashboardPageName } from "@src/api/types/dashboards";
import { useSelector } from "@src/store";
import {
  IExperienceDashboardFilters,
  initialDashboardFiltersState,
} from "@src/store/types/dashboardFilters";
import {
  BarMetricData,
  GraphChangeLabelStatus,
  LineMetricData,
  StackedBarMetricData,
} from "@src/types/analytics";
import { isStringMatchingDatePattern } from "@src/utils/isStringMatchingDatePattern";
import { genericDescSort } from "@src/utils/sortings";
import { UseQueryResult } from "@tanstack/react-query";

export const shouldShowProfitData = (metrics: BaseTinybirdMetricsType[]) => {
  return metrics.some(
    (metric) =>
      "pct_revenue_with_cogs" in metric &&
      metric.pct_revenue_with_cogs?.value !== null &&
      metric.pct_revenue_with_cogs?.value > 0
  );
};

export const shouldShowProfitPlaceholder = (
  shouldShowProfit: boolean | undefined,
  config: GraphConfig
): boolean => {
  return (
    config.metrics.some((metric) => {
      const isStandardEvent = StandardEventList.some(
        (standardMetric) => standardMetric === metric
      );
      return metric.includes("profit") && !isStandardEvent;
    }) && !shouldShowProfit
  );
};

export const orderFiltersByValue = (data: BarMetricData[]) => {
  const allTogether: any[] = [];
  const filterMethodList = data
    .map((metric: BarMetricData) => metric.filterMethod)
    .filter((val, id, array) => array.indexOf(val) === id);

  filterMethodList.forEach((value) => {
    allTogether.push({ filterMethod: value as string, value: 0 });
  });

  allTogether.forEach((data1: BarMetricData, index: number) => {
    const groupsByRate = data.filter(
      (group: BarMetricData) => group.filterMethod === data1.filterMethod
    );
    groupsByRate.forEach((groupData: BarMetricData) => {
      Object.assign(allTogether[index], {
        value: (allTogether[index].value += groupData.value),
      });
    });
  });

  return allTogether.sort(genericDescSort);
};

export const shouldShowInsufficientCogsWarning = (
  data: UseQueryResult<AnalyzeTinybirdResourceResponseBodyType>[]
) => {
  if (!data?.[0]?.data?.metrics) {
    return false;
  }

  const metrics = data[0].data.metrics;
  // only show if cogs is over 0 but less than 90
  return metrics.some(
    (metric) =>
      "pct_revenue_with_cogs" in metric &&
      metric.pct_revenue_with_cogs?.value > 0 &&
      metric.pct_revenue_with_cogs?.value < 0.9
  );
};

export const getDefaultGroupGraphFilterValueTb = (
  data: BarMetricData[] | LineMetricData[] | undefined
) => {
  if (data && data.length > 0) {
    const mostOrderRate = orderFiltersByValue(data as BarMetricData[]);
    const firstDefinedMethod = mostOrderRate.findIndex(
      (val) => val.filterMethod !== undefined || val.filterMethod! == null
    );
    if (firstDefinedMethod !== -1) {
      return mostOrderRate[firstDefinedMethod].filterMethod;
    }
  } else {
    return "";
  }
};

export const getNewGraphConfig = (
  pageName: DashboardPageName,
  includeTestOrders?: boolean
): GraphConfig[] => {
  switch (pageName) {
    case "overview":
      return newOverviewGraphConfig;
    case "profit":
      return newProfitGraphConfig;
    case "revenue":
      return newRevenueGraphConfig;
    case "shipping":
      return newShippingGraphConfig;
    case "orders":
      return newOrdersGraphConfig;
    case "discounts":
      return newDiscountGraphConfig;
    case "productGroups":
      return newProductGroupsGraphConfig;
    case "subscriptions":
      return newSubscriptionsGraphConfig;
    case "dataReconciliation":
      return newDataReconciliationGraphConfig;
    case "conversion":
      return newConversionGraphConfig;
    case "customMetrics":
      return newCustomMetricsGraphConfig;
    case "postTestMetrics":
      return includeTestOrders
        ? newPostTestMetricsIncludeInTestOrdersGraphConfig
        : newPostTestMetricsGraphConfig;
    default:
      return newOverviewGraphConfig;
  }
};

/**
 * Pages that append upsell / post-purchase / onsite-upsell graph configs on top
 * of their base config, each gated on whether the experience has that offer type.
 *
 * `onsiteUpsell` is optional because not every page has an onsite variant wired
 * up yet — `onsiteUpsellRevenueGraphConfig` and `onsiteUpsellConversionGraphConfig`
 * exist in ig-graphs but are not attached to the revenue/conversion pages.
 */
export const UPSELL_EXTRA_CONFIGS: Partial<
  Record<
    DashboardPageName,
    {
      upsell: GraphConfig[];
      postPurchase: GraphConfig[];
      onsiteUpsell?: GraphConfig[];
    }
  >
> = {
  revenue: {
    upsell: checkoutUpsellRevenueGraphConfig,
    postPurchase: postPurchaseRevenueGraphConfig,
  },
  conversion: {
    upsell: checkoutUpsellConversionGraphConfig,
    postPurchase: postPurchaseConversionGraphConfig,
  },
  productGroups: {
    upsell: checkoutUpsellProductsGraphConfig,
    postPurchase: postPurchaseProductsGraphConfig,
    // Required for the onsite upsell columns to be populated at all, not merely
    // to add a graph: ep_variation_products gates the upstream order-line nodes
    // that compute net_revenue_from_onsite_upsell on a substring match over the
    // metrics array (`"onsite_upsell" in metrics`). Without a metric carrying
    // that substring the breakdown columns project empty even though
    // includeOnsiteUpsellBreakdown is set on the payload.
    onsiteUpsell: onsiteUpsellProductsGraphConfig,
  },
};

/**
 * Append the offer-scoped graph configs a page supports, gated on which offer
 * types this experience actually runs. Returns `base` untouched for pages that
 * declare no extras.
 */
export const withUpsellExtraConfigs = (
  base: GraphConfig[],
  pageName: DashboardPageName,
  offers: {
    hasUpsellBlocks: boolean;
    hasPostPurchaseUpsell: boolean;
    hasOnsiteUpsell: boolean;
  }
): GraphConfig[] => {
  const extras = UPSELL_EXTRA_CONFIGS[pageName];
  if (!extras) {
    return base;
  }

  return [
    ...base,
    ...(offers.hasUpsellBlocks ? extras.upsell : []),
    ...(offers.hasPostPurchaseUpsell ? extras.postPurchase : []),
    ...(offers.hasOnsiteUpsell && extras.onsiteUpsell
      ? extras.onsiteUpsell
      : []),
  ];
};

export const dataReconciliationReasonDescriptionMapping = {
  "Subscription refill":
    "The order is a subscription refill (i.e., not the first order in the subscription series)",
  "Is gift card only":
    "The order contained gift cards only (revenue from the sale of gift cards is excluded from analytics)",
  "Draft order": "The order is a Shopify draft order",
  "Non-online store order":
    "The order originated from a channel outside of the Shopify online store (e.g., Shopify POS, Instagram Shopping, etc.)",
  "Order could not be matched to a session":
    "The order could not be mapped to a corresponding session",
  "Visitor has opened Intelligems preview mode":
    "The visitor who placed this order is excluded because they have opened Intelligems' preview mode before",
  "Visitor excluded due to mutual exclusion":
    "The visitor was excluded because they were included in a different mutually exclusive experiment",
  "Visitor was excluded by audience targeting":
    "The visitor who placed this order is excluded by audience targeting",
  "Visitor was excluded from analytics by audience targeting":
    "The visitor who placed this order is excluded from analytics by audience targeting",
  "Visitor did not enter experiment during the session when order was created":
    "The visitor placed the order during a session in which they did not enter the experiment",
  "Visitor never entered the experiment":
    "The visitor never entered the experiment",
  "Order created before visitor entered experiment":
    "The order was created before the visitor entered the experiment",
  "Order created during experiment pause":
    "The order was placed while the experiment was paused",
  "Session started during experiment pause":
    "The session in which the order was placed started while the experiment was paused",
  "Order was refunded or voided": "The order was refunded or voided",
  "Net revenue is <= 0":
    "The net revenue associated with the order is less than or equal to 0",
  "Filtered out: outlier":
    "The order is filtered out because it is an outlier in net revenue",
  "Filtered out: country":
    "The order is filtered out by the country code filter",
  "Filtered out: device type":
    "The order is filtered out by the device type filter",
  "Filtered out: new vs. returning visitor":
    "The order is filtered out by the visitor type filter",
  "Visitor's IP address is excluded":
    "The visitor that placed the order had an IP address that was part of an exclusion list",
  "Filtered out: order has tags that are excluded":
    "The order is filtered out because it contains an order tag that was part of an exclusion list",
  "Filtered out: traffic sources or channels":
    "The order is filtered out by the traffic sources or channels filter",
  "Filtered out: query param filter":
    "The order is filtered out by the query parameters filter",
  "Filtered out: custom event":
    "The order is filtered out by the custom event filter",
  "Filtered out: added to cart filter":
    "The order is filtered out by the added to cart filter",
  "Filtered out: did not reach checkout stage":
    "The order is filtered out by the reached checkout stage filter",
  "Filtered out: did not visit product page":
    "The order is filtered out by the did not visit product page filter",
  "Filtered out: did not visit collection page":
    "The order is filtered out by the did not visit collection page filter",
  "Filtered out: order did not include products from filter":
    "The order is filtered out because it did not contain any products from the specified products only filter",
  "Filtered out: order did not include experiment products":
    "The order is filtered out because it did not contain any of the products included in the experiment",
  "Filtered out: did not view checkout upsell":
    "The order is filtered out because the visitor did not view a checkout upsell during the session",
  "Associated test group has been deleted":
    "The test group this visitor was placed into has been deleted",
  "Other subscription order":
    "The order is a subscription order that is neither the first order nor a refill",
  "Visitor entered experiment before the analysis date range":
    "The visitor entered the experiment before the start of the analysis date range (i.e. experiment may have been reset)",
  "Session started before the analysis date range":
    "The session in which the order was placed started before the selected date range",
  "Filtered out: landing page":
    "The order is filtered out by the landing page filter",
  "Filtered out: net revenue range":
    "The order is filtered out by the minimum or maximum net revenue filter",
  "Filtered out: shipping revenue range":
    "The order is filtered out by the minimum or maximum shipping revenue filter",
  "Filtered out: shipping method":
    "The order is filtered out by the shipping method filter",
  "Filtered out: new vs. returning customer":
    "The order is filtered out by the customer type filter",
  "Filtered out: order did not include checkout upsell products":
    "The order is filtered out because it did not contain any checkout upsell products",
  "Filtered out: order did not include onsite upsell products":
    "The order is filtered out because it did not contain any onsite upsell products",
  "Filtered out: order did not include post-purchase offer products":
    "The order is filtered out because it did not contain any post-purchase offer products",
  "Not excluded": "The order is included in experiment analytics",
};

export const getExperienceTotal = (
  dashboardData: UseQueryResult<AnalyzeTinybirdResourceResponseBodyType>[],
  dataKey: "n_visitors" | "n_orders" | "net_revenue"
) => {
  const revDataset = dashboardData.find(
    (metric) => metric.data?.datasetId === "variation_overview"
  );
  if (revDataset) {
    const metrics = revDataset.data?.metrics?.map(
      (metric) => metric[dataKey]?.value || 0
    );
    if (metrics) {
      return metrics.reduce(
        (prev, curr) => (prev as number) + (curr as number),
        0
      ) as number;
    } else {
      return 0;
    }
  } else {
    return 0;
  }
};

export const getReferenceLinesFromExperienceActions = (
  experienceActions: ExperienceActionDto[]
) => {
  return experienceActions
    .slice(0, experienceActions.length - 1)
    .filter((action) => action.action !== "end");
};

export const getGridWidthCalc = (innerWidth: number, numVariations: number) => {
  if (numVariations <= 4) {
    return innerWidth <= 1000 ? 6 : innerWidth < 1673 ? 4 : 3;
  } else if (numVariations <= 6) {
    return innerWidth < 1480 ? 6 : 4;

    // return 4;
  } else {
    return 6;
  }
};

export const getIgHorizStackedBarColor = (
  config: GraphConfig,
  index: number,
  unit: string
) => {
  if (config.title[0].includes("Product Group")) {
    if (unit === "All Others") {
      return NewHorizStackedBarColors.gray[0];
    }
    if (unit === "<No Product Group>") {
      return NewHorizStackedBarColors.gray[1];
    }
    return NewHorizStackedBarColors.productGroups[index];
  } else {
    return NewHorizStackedBarColors.base[index];
  }
};

export const getIgHorizStackedBarName = (unit: string, config: GraphConfig) => {
  if (config.title[0].includes("Product Group")) {
    return unit;
  } else {
    return `${unit}${config.maxGroupAmt && config.maxGroupAmt === parseFloat(unit) ? "+" : ""} Unit${parseFloat(unit) > 1 || parseFloat(unit) === 0 ? "s" : ""}`;
  }
};

export const getShouldShowAxisDecimals = (
  data: BarMetricData[],
  config: GraphConfig
) => {
  switch (config.dataTypes[0]) {
    case "currency":
      if (data.some((d) => d.value % 1 === 0)) {
        return false;
      }
      // if the largest range between array values is greater than 10, dont show decimals)
      return (
        Math.max(...data.map((d) => d.value)) -
          Math.min(...data.map((d) => d.value)) <=
        10
      );

    case "percentTwoDecimal":
      return Math.max(...data.map((d) => d.value * 100)) < 10;
    default:
      return data.some((d) => d.value % 1 !== 0) && data.length > 1;
  }
};

// ONLY USED FOR AOV CHARTS
export const getStackedBarShouldShowAxisDecimals = (
  data: StackedBarMetricData[],
  config: GraphConfig
) => {
  // @ts-ignore
  const totaledData = data.map(
    (d) =>
      (d[config.stackedBarLegendTitles![0]] as number) +
      (d[config.stackedBarLegendTitles![1]] as number)
  );
  switch (config.dataTypes[0]) {
    case "currency":
      return Math.max(...totaledData.map((d) => d)) < 5;
    default:
      return totaledData.some((d) => d % 1 !== 0) && data.length > 1;
  }
};

export const chartTooltips = {
  "AOV (Net Revenue per Order)":
    "Net revenue divided by number of orders (also known as average order value, AOV).",
  "Conversion Rate": "Orders divided by unique site visitors.",
  "Net Shipping Revenue per Order":
    "Revenue from shipping fees, net of discounts, per order",
  "Net Product Revenue per Order":
    "Average net revenue per order generated by product sales alone (i.e. excludes shipping revenue)",
  "Revenue per Visitor":
    "Net revenue divided by number of unique site visitors. Net revenue includes product and shipping revenue, net of discounts.",
  "Profit per Visitor":
    "Gross profit divided by number of unique site visitors. Gross profit includes product and shipping revenue, net of discounts, COGS, cost of shipping, and transaction fees.",
  Visitors: "Number of unique site visitors in the experience.",
  Orders: "Number of orders placed by visitors in the experience.",
  "Product Revenue per Order":
    "Average net revenue per order generated by product sales alone (i.e. excludes shipping revenue). This is the product revenue portion of AOV.",
  "Shipping Revenue per Order":
    "Average net revenue per order generated by shipping charges. This is the shipping revenue portion of AOV.",
  "Total Avg Order Value":
    "Net revenue per order (including both product and shipping revenue).",
  "Total Average Order Value":
    "Net revenue per order (including both product and shipping revenue).",
  "Subscription AOV":
    "Average order value from new subscription orders (average net revenue per order for new subscription orders).",
  "Avg Units per Order": "Average quantity of product units per order.",
  "Subscription Avg Units per Order":
    "Average quantity of product units contained in each new subscription order.",
  "Avg Product Revenue per Unit":
    "Average net price for a single unit (net of discounts).",
  "Subscription Product Revenue per Unit":
    "Average net price for a single unit in a subscription order (net of discounts).",
  "Gross Margin": "Gross profit divided by net revenue.",
  "ATC Rate":
    "% of visitors who added a product to cart. If filtered to target products only, then a visitor counts only if they added a target product to cart.",
  "Abandoned Cart Rate":
    "% of visitors who add to cart but do not make a purchase.",
  "Abandoned Checkout Rate":
    "% of visitors who started checkout but do not make a purchase.",
  "Begin Checkout Rate":
    "% of visitors who started checkout. If filtered to target products only, then a visitor counts only if they started checkout with a target product in the cart.",
  "% of Orders with Free Shipping":
    "Percentage of orders that had no associated net shipping revenue.",
  "Order Value Distribution":
    'Distribution of orders. If "Net Product Revenue" is selected, the distribution is based on net product revenue (i.e., excluding shipping revenue). If "Net Revenue" is selected, the distribution is based on total net revenue.',
  "Cumulative % of Orders":
    'Cumulative % of orders with revenue less than or equal to the value of the X-axis. For example, if the value at X = $100 is 50%, that means 50% of orders have revenue less than or equal to $100. If "Net Product Revenue" is selected, the X-axis is based on net product revenue (i.e., excluding shipping revenue). If "Net Revenue" is selected, the X-axis is based on total net revenue.',
  "Orders Over Time": "Number of orders placed on the site by creation time.",
  "% of Orders with Discount":
    "Number of orders with discount applied divided by total orders placed by visitors in the experience.",
  "% Orders Discounted":
    "Number of orders with discount applied divided by total orders placed by visitors in the experience.",
  "Avg Discount":
    'Average discount per order. If "Discounted Orders Only" is selected, this is the average discount considering orders with a discount only. If "All Orders" is selected, this is the average discount across all orders, including 0s from orders with no discount.',
  "Discount % of Gross Revenue":
    'Discounts divided by gross revenue. If "Discounted Orders Only" is selected, the denominator is gross revenue from orders with a discount. If "All Orders" is selected, the denominator is total gross revenue, including all orders.',
  "Discount % of Gross Profit":
    'Discounts divided by gross profit. If "Discounted Orders Only" is selected, the denominator is gross profit from orders with a discount. If "All Orders" is selected, the denominator is total gross profit, including all orders.',
  "% Orders by Unit Quantity":
    "Breakdown of orders by the quantity of units contained in each order.",
  "% Net Revenue by Order Unit Quantity":
    "Breakdown of net revenue by the quantity of units contained in each order.",
  "Total Profit":
    "Gross profit. Gross profit includes product and shipping revenue, net of discounts, COGS, cost of shipping, and transaction fees.",
  "Subscription % of Orders":
    "Percentage of orders that contained a subscription. Only subscription first orders are counted, automatic refill orders are excluded.",
  "Subscription Conversion Rate":
    "Number of new subscription orders divided by number of unique site visitors.",
  "Subscription Revenue per Visitor":
    "Total revenue from new subscription orders divided by number of unique site visitors. Revenue includes both product and shipping revenue, net of discounts.",
  "Subscription Profit per Visitor":
    "Total profit from new subscription orders divided by number of unique site visitors. Profit includes product and shipping revenue, net of discounts, COGS, and cost of shipping.",
  "Product Group Mix":
    "Breakdown of net product revenue or unit quantity (depending on which is selected) by product group.",
  "Subscription Orders per Visitor":
    "Number of new subscription orders divided by number of unique site visitors.",
  "View Collection Rate": "% of visitors who viewed a collection page.",
  "View Product Rate": "% of visitors who viewed a product detail page (PDP).",
  "View Product Page Rate":
    "% of visitors who viewed a product page. If filtered to target products only, then a visitor counts only if they viewed a target product page.",
  "Post-Test Profit Per Visitor":
    "Gross profit from additional orders placed after the test ends, divided by unique visitors exposed during the test. Measures the long-term profit impact of each visitor included in the test.",
  "Post-Test Revenue Per Visitor":
    "Revenue from additional orders placed after the test ends, divided by unique visitors exposed during the test. Measures the long-term revenue impact of each visitor included in the test.",
  "Post-Test Profit Per Customer":
    "Gross profit from additional orders placed after the test ends, divided by customers that converted during the test.",
  "Post-Test Revenue Per Customer":
    "Revenue from additional orders placed after the test ends, divided by customers that converted during the test.",
  "Post-Test Orders Per Customer":
    "# of additional orders placed after the test ends, divided by unique customers that converted during the test.",
  "Total Profit Per Visitor":
    "Gross profit from in-test orders and additional orders after the test ends, divided by unique visitors exposed during the test. Measures the long-term profit impact of each visitor included in the test.",
  "Total Revenue Per Visitor":
    "Revenue from in-test orders and orders after the test ends, divided by unique visitors exposed during the test. Measures the long-term profit impact of each visitor included in the test.",
  "Total Profit Per Customer":
    "Gross profit from in-test orders and additional orders placed after the test ends, divided by customers that converted during the test.",
  "Total Revenue Per Customer":
    "Revenue from in-test orders and additional orders placed after the test ends, divided by customers that converted during the test.",
  "Total Orders Per Customer":
    "# of in-test orders and additional orders placed after the test ends, divided by customers that converted during the test.",
  "In-Test Customers":
    "Customers that converted during the test. % change figure is normalized for traffic allocation.",
  "Post-Test Profit":
    "Gross profit from additional orders placed after the test ends.",
  "Post-Test Revenue":
    "Revenue from additional orders placed after the test ends.",
  "% Customers with Post-Test Order":
    "Share of in-test customers that placed at least one additional order after the test ended.",
  "Checkout Upsell Revenue": "Net revenue generated from checkout upsells",
  "Post Purchase Upsell Revenue":
    "Net revenue generated from post purchase upsell offers",
  "Orders with Checkout Upsell":
    "Number of orders that included a checkout upsell",
  "Orders with Post Purchase Upsell":
    "Number of orders that included a post purchase upsell offer",
  "% of Orders with Checkout Upsell":
    "Percentage of orders with a checkout upsell",
  "% of Orders with Post Purchase Upsell":
    "Percentage of orders with a post purchase upsell offer",
};

export const transformDashboardDataToGlobalMetric = (
  tableData: UseQueryResult<AnalyzeTinybirdResourceResponseBodyType>[],
  graphConfig: GraphConfig
): GraphGlobalMetric[] | undefined => {
  const filteredData = filterDashboardDataTb(
    tableData,
    "variation_overview",
    graphConfig
  );

  if (!filteredData?.[0]) {
    return;
  }

  const globalMetrics = graphConfig.metrics.map((metric) => {
    return {
      label: "Total",
      value: filteredData?.[0]?.[metric]?.value,
    };
  });

  return globalMetrics;
};

export const METRIC_TO_LEGEND_LABEL: Record<string, string> = {
  abandoned_cart_rate: "Abandoned Cart Rate",
  abandoned_checkout_rate: "Abandoned Checkout Rate",
  add_to_cart_rate: "Add-to-Cart Rate",
  aov: "AOV",
  aov_pre_discount: "AOV Pre-Discount",
  average_discount: "Average Discount",
  average_units_per_order: "Average Units Per Order",
  avg_unit_price: "Average Unit Price",
  bounce_rate: "Bounce Rate",
  checkout_begin_rate: "Begin Checkout Rate",
  conversion_rate: "Conversion Rate",
  current_period: "Current Period",
  gross_margin_pct: "Gross Margin",
  gross_profit: "Gross Profit",
  gross_profit_per_visitor: "Profit per Visitor",
  gross_revenue: "Revenue",
  index_lift: "Index (as % of typical full day)",
  median_order_value: "Median Order Value",
  n_orders: "Orders",
  n_orders_with_checkout_upsell_product: "Orders with Checkout Upsell",
  n_orders_with_onsite_upsell_product: "Orders with Onsite Upsell",
  n_orders_with_post_purchase_product: "Orders with Post Purchase Upsell",
  n_visitors: "Visitors",
  net_product_revenue: "Net Product Revenue",
  net_revenue: "Net Revenue",
  net_revenue_from_checkout_upsell: "Checkout Upsell Revenue",
  net_revenue_from_onsite_upsell: "Onsite Upsell Revenue",
  net_revenue_from_post_purchase_offer: "Post Purchase Upsell Revenue",
  net_revenue_per_order: "Revenue per Order",
  net_revenue_per_visitor: "Revenue per Visitor",
  net_shipping_revenue_per_order: "Shipping Revenue per Order",
  pct_gross_profit_by_audience: "% of Profit",
  pct_net_revenue_by_audience: "% of Revenue",
  pct_orders_by_audience: "% of Orders",
  pct_orders_by_unit_quantity: "% of Orders",
  pct_orders_with_free_shipping: "% Orders with Free Shipping",
  pct_profit_by_unit_quantity: "% of Profit",
  pct_revenue_by_unit_quantity: "% of Revenue",
  pct_visitors_by_audience: "% of Visitors",
  previous_period: "Previous Period",
  viewed_collection_page_rate: "View Collection Rate",
  viewed_product_page_rate: "View Product Rate",
};

export enum MetricToLegendLabelEnumBenchmark {
  n_orders = "Orders",
  net_revenue = "Revenue",
  net_revenue_per_order = "Revenue per Order",
  net_revenue_per_visitor = "Revenue per Visitor",
  n_visitors = "Visitors",
  avg_unit_price = "Average Unit Price",
  net_product_revenue = "Net Product Revenue",
  net_revenue_lift = "Revenue (as % of typical full day)",
  index_lift = "Index (as % of typical full day)",
}

export const formatChartLabel = (
  label: string | number | undefined,
  dateFormat?: string
) => {
  if (label === undefined) {
    return "";
  } else if (typeof label === "number") {
    return label;
  } else if (isStringMatchingDatePattern(label) && dateFormat) {
    return formatDateLabel(label, dateFormat);
  } else {
    return label;
  }
};

const formatDateLabel = (label: string, dateFormat: string) => {
  // Check label ends with " hh:ss:mm"
  if (DateTime.fromSQL(label).isValid) {
    return DateTime.fromSQL(label).toFormat(dateFormat);
  }
  // Check label is a valid date
  else if (DateTime.fromISO(label).isValid) {
    return DateTime.fromISO(label).toFormat(dateFormat);
  } else {
    return label;
  }
};

export const hasFiltersBeenModified = (
  dashboardFilters: IExperienceDashboardFilters
) => {
  const reducedDashFilters: Partial<IExperienceDashboardFilters> = {
    channels: dashboardFilters.channels,
    deviceType: dashboardFilters.deviceType,
    visitorType: dashboardFilters.visitorType,
    startTime: dashboardFilters.startTime,
    endTime: dashboardFilters.endTime,
    isZScoreEnabled: dashboardFilters.isZScoreEnabled,
  };

  const reducedDashInitials: Partial<IExperienceDashboardFilters> = {
    channels: initialDashboardFiltersState.channels,
    deviceType: initialDashboardFiltersState.deviceType,
    visitorType: initialDashboardFiltersState.visitorType,
    startTime: initialDashboardFiltersState.startTime,
    endTime: initialDashboardFiltersState.endTime,
    isZScoreEnabled: initialDashboardFiltersState.isZScoreEnabled,
  };
  return !isEqual(reducedDashFilters, reducedDashInitials);
};

export const getGraphChangeLabelColor = (
  status: GraphChangeLabelStatus,
  theme: Theme
) => {
  switch (status) {
    case "negative":
      return theme.palette.text.tonal.error;
    case "neutral":
      return theme.palette.text.primary;
    case "positive":
      return theme.palette.text.tonal.success;
  }
};

export const getGraphChangeLabelStatus = (
  changeValue: number,
  isInverted: boolean
): GraphChangeLabelStatus => {
  if (changeValue === 0) {
    return "neutral";
  }
  const isPositiveChange = changeValue > 0;
  const isPositive = isInverted ? !isPositiveChange : isPositiveChange;
  return isPositive ? "positive" : "negative";
};
